import { fetcher } from "@fwa/src/services/apiClient";
import { fwsUrlFundraisingPageType } from "@fwa/src/services/fundraisingPageTypeService";
import { fwsUrlPage } from "@fwa/src/services/pageService";
import {
  type FundraisingPageTypeType,
  type StepsType,
  type SuperTeamPageType,
  type TeamPageType,
} from "@fwa/src/types";
import { isBrowser } from "./browserUtils";

const stepDefaultAccount = {
  machineName: "account",
  stepTitle: "Account",
  skip: true,
  includeInTracker: false,
};

const stepDefaultDoing = {
  machineName: "type",
  stepTitle: "Doing",
  skip: false,
  includeInTracker: true,
};

const stepDefaultActivity = {
  machineName: "activity",
  stepTitle: "Activity",
  skip: false,
  includeInTracker: true,
};

const stepDefaultMotivation = {
  machineName: "motivation",
  stepTitle: "Motivation",
  skip: false,
  includeInTracker: true,
};

const stepDefaultPage = {
  machineName: "page",
  stepTitle: "Page",
  skip: false,
  includeInTracker: true,
};

export const defaultSteps: StepsType[] = [
  stepDefaultAccount,
  stepDefaultDoing,
  stepDefaultActivity,
  stepDefaultMotivation,
  stepDefaultPage,
];

export const updateStepsFromFundraisingPageType = ({
  fundraisingPageType,
  newUser,
  isSignUpPage,
}: {
  fundraisingPageType?: FundraisingPageTypeType;
  newUser: boolean;
  isSignUpPage: boolean;
}) => {
  const isDefaultPageType =
    !fundraisingPageType || fundraisingPageType?.pageTypeName === "default";
  const foundActivityStep = fundraisingPageType?.steps?.find(
    (item) => item.machineName === "activity",
  );
  const foundMotivationStep = fundraisingPageType?.steps?.find(
    (item) => item.machineName === "motivation",
  );

  // if we only have the page step to show, then we don't show the page step in the tracker
  // lets figure out if any other steps are visible tracker.
  const shouldShowPageStepInTracker =
    isDefaultPageType || // doing step is visible for default pagetype, it is hidden for events/activities pagetypes
    (foundActivityStep?.includeInTracker ?? true) || // activity step revealed in tracker
    (foundMotivationStep?.includeInTracker ?? true); // motivation steps revealed in tracker

  const nextSteps: StepsType[] = [
    {
      ...stepDefaultAccount,
      // we only want to show the account step if the user has come from signup
      skip: !isSignUpPage,
      includeInTracker: newUser,
    },
    {
      ...stepDefaultDoing,
      skip: !isDefaultPageType,
      includeInTracker: isDefaultPageType,
    },
    {
      ...stepDefaultActivity,
      skip: foundActivityStep?.skip ?? stepDefaultActivity.skip,
      includeInTracker:
        foundActivityStep?.includeInTracker ??
        stepDefaultActivity.includeInTracker,
    },
    {
      ...stepDefaultMotivation,
      skip: foundMotivationStep?.skip ?? stepDefaultMotivation.skip,
      includeInTracker:
        foundMotivationStep?.includeInTracker ??
        stepDefaultMotivation.includeInTracker,
      fieldOverrides: foundMotivationStep?.fieldOverrides,
    },
    {
      ...stepDefaultPage,
      includeInTracker: shouldShowPageStepInTracker,
    },
  ];
  return nextSteps;
};

const getTeamDataFromUrl = async ({
  onError,
}: {
  onError: (err: Error) => void;
}): Promise<TeamPageType | void> => {
  if (!isBrowser) return undefined;
  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams.get("team-uuid")) {
    return fetcher(
      fwsUrlPage({
        pageId: searchParams.get("team-uuid")! || "",
      }),
    )
      .then((res) => res as TeamPageType)
      .catch((err) => {
        onError(err as Error);
      });
  }
  return undefined;
};

const getSuperTeamDataFromUrl = async ({
  onError,
}: {
  onError: (err: Error) => void;
}): Promise<SuperTeamPageType | void> => {
  if (!isBrowser) return undefined;
  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams.get("super-team-uuid")) {
    return fetcher(
      fwsUrlPage({
        pageId: searchParams.get("super-team-uuid")!,
      }),
    )
      .then((res) => res as SuperTeamPageType)
      .catch((err) => {
        onError(err as Error);
      });
  }
  return undefined;
};

// our hidiously complicated way of getting page type the new page using in priority:
// 1) the pagetype passed into the url by ID
// 2) the pagetype of the superteam in the url
// 3) the pagetype of the team in the url
// 4) default
const getFundraisingPageTypeDataFromUrl = async ({
  onError,
  superTeamFundraisingPageTypeId,
  teamLeaderFundraisingPageTypeId,
}: {
  onError: (err: Error) => void;
  superTeamFundraisingPageTypeId?: string;
  teamLeaderFundraisingPageTypeId?: string;
}): Promise<FundraisingPageTypeType | void> => {
  if (!isBrowser) return undefined;
  const searchParams = new URLSearchParams(window.location.search);
  const pageTypeIdFromUrl: string | null = searchParams.get(
    "fundraising_page_type",
  );
  const anyPageTypeId =
    pageTypeIdFromUrl ||
    superTeamFundraisingPageTypeId ||
    teamLeaderFundraisingPageTypeId ||
    "default";

  if (anyPageTypeId) {
    return fetcher(
      fwsUrlFundraisingPageType({
        nameOrUniqueId: anyPageTypeId || "",
      }),
    )
      .then((res) => res as FundraisingPageTypeType)
      .catch((err) => {
        onError(err as Error);
      });
  }
  return undefined;
};

type DataFromQueryParam = {
  teamData: TeamPageType | undefined;
  superTeamData: SuperTeamPageType | undefined;
  fundraisingPageTypeData: FundraisingPageTypeType | undefined;
  errorMessage: string;
};

// if we already have data in the state don't bother doing the API calls just use state

export const getFwsEntitiesFromQueryParams =
  async (): Promise<DataFromQueryParam> => {
    let errorMessage = "";

    const teamData: TeamPageType | undefined =
      (await getTeamDataFromUrl({
        onError: () => {
          errorMessage =
            "Sorry we couldn't find the team at the moment, but don't worry you can create the page and then join it to a team";
        },
      })) || undefined;

    const superTeamData: SuperTeamPageType | undefined =
      (await getSuperTeamDataFromUrl({
        onError: () => {
          errorMessage =
            "Sorry we couldn't find the team at the moment, but don't worry you can create the page and then join it to a team";
        },
      })) || undefined;

    const fundraisingPageTypeData: FundraisingPageTypeType | undefined =
      (await getFundraisingPageTypeDataFromUrl({
        superTeamFundraisingPageTypeId:
          superTeamData?.fundraisingPageType?.uniqueId,
        teamLeaderFundraisingPageTypeId:
          teamData?.teamLeaderPage?.fundraisingPageType?.uniqueId,
        onError: () => {
          // don't display error message just continue with signup
        },
      })) || undefined;

    return { teamData, superTeamData, fundraisingPageTypeData, errorMessage };
  };

export function getNextStep(currentSteps: StepsType[], currentStep: number) {
  const stepJumpIndex: number = [...currentSteps]
    .slice(currentStep + 1)
    .findIndex((item) => !item.skip);

  const nextStepIndex = currentStep + stepJumpIndex + 1;
  return nextStepIndex;
}

export function hasPreviousStep(
  currentSteps: StepsType[],
  currentStep: number,
) {
  const previousIndex: number = [...currentSteps]
    .slice(0, currentStep)
    .findLastIndex((item) => !item.skip && item.machineName !== "account");
  // account which is a different form already submitted so we can't go back to that
  return previousIndex >= 0;
}

export function getPreviousStep(
  currentSteps: StepsType[],
  currentStep: number,
) {
  const previousIndex: number = [...currentSteps]
    .slice(0, currentStep)
    .findLastIndex((item) => !item.skip && item.machineName !== "account");
  return previousIndex;
}

// when comming from /signup a param newUser should be set to true
export function isNewUser(): boolean {
  if (!isBrowser) return false;
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get("newUser") === "true" || false;
}
